import React, { useState } from 'react';
import { ClientCreate } from '../../api/models/ClientCreate';
import { ManagementOauthApi } from '../../api/apis';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { useI18n } from '../../contexts/I18nContext';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClientCreated: (client: ClientCreate) => void;
}

/** A new client: its name. SIREN, status and access are set in its page afterwards. */
export const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  onClientCreated
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const validate = (): boolean => {
    const name = clientName.trim();
    let message: string | null = null;
    if (!name) message = t('errors.fieldRequired');
    else if (name.length < 2) message = t('clients.create.nameMin');
    else if (name.length > 256) message = t('clients.create.nameMax');
    setNameError(message);
    return message === null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    setIsLoading(true);
    try {
      const managementApi = new ManagementOauthApi(apiClient.getConfiguration());
      const clientData: ClientCreate = { name: clientName.trim() };
      await managementApi.apiAddClientV2V2ClientPost({ clientCreate: clientData });
      // Bust the client cache so the other forms see the new client at once.
      clientService.clearCache();
      onClientCreated(clientData);
      setClientName('');
      setNameError(null);
      onClose();
    } catch (err: any) {
      console.error('[CREATE CLIENT] Error creating client:', err);
      let errorMessage = t('errors.failedToSave');
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        errorMessage = detail.map((d: any) => `${d.loc?.join('.') || 'field'}: ${d.msg}`).join(', ');
      } else if (typeof detail === 'string') {
        if (detail.includes('duplicate key') || detail.includes('already exists')) {
          errorMessage = t('clients.create.duplicate', { name: clientName.trim() });
        } else if (detail.includes('psycopg2')) {
          errorMessage = detail.split('\n')[0];
        } else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setClientName('');
    setNameError(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const name = clientName.trim();

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="config-template-modal tpc clc" data-testid="modal-create-client" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('clients.addClient')}</h2>
              <div className="tpd-title-sub"><span>{t('clients.create.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={handleClose} disabled={isLoading} aria-label={t('common.close')}>×</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {error && <div className="error-message" role="alert">{error}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('clients.information')}</h3>
                <p>{t('clients.create.intro')}</p>
              </header>
              <div className="cu-field">
                <label htmlFor="clientName">{t('clients.clientName')} *</label>
                <input
                  type="text"
                  id="clientName"
                  data-testid="field-client-name"
                  value={clientName}
                  onChange={(e) => { setClientName(e.target.value); if (nameError) setNameError(null); }}
                  className={`cu-input${nameError ? ' is-invalid' : ''}`}
                  placeholder={t('clients.namePlaceholderDetailed')}
                  disabled={isLoading}
                  autoFocus
                  maxLength={256}
                />
                {nameError ? <span className="cu-error">{nameError}</span> : <span className="cu-hint">{t('clients.create.nameHint')}</span>}
              </div>
              <p className="cu-note">{t('clients.create.note')}</p>
            </section>
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary">
              {name ? <strong>{name}</strong> : <span className="tpd-muted">{t('clients.create.summaryEmpty')}</span>}
            </div>
            <div className="tpc-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={handleClose} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isLoading}>
                {isLoading ? t('clients.create.creating') : t('clients.create.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
